import { AuthOutlet, SessionTimeoutMonitor } from "@certn/auth";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router";

import { BillingRoutes } from "./billing/BillingRoutes";
import { SettingsRoutes } from "./settings/SettingsRoutes";
import { RequireSettingsAccess } from "./settings/RequireSettingsAccess";
import { PageNotFoundError } from "../components/PageNotFoundError";
import { useCanViewCaseDetails } from "../components/permissions/Permissions";
import { BasePage } from "../pages/BasePage";
import { BootstrapAuthorizedInstrumentation } from "../providers/BootstrapAuthorizedInstrumentation";
import { ClientPortalBootstrapProvider } from "../providers/ClientPortalBootstrapProvider";
import { ApplyLinkDetails } from "../pages/apply-links/ApplyLinkDetails";
import { ApplyLinkListView } from "../pages/apply-links/ApplyLinkListView";
import { CreateApplyLink } from "../pages/apply-links/CreateApplyLink";
import { CaseDetailsPage } from "../pages/case-details/CaseDetailsPage";
import { DashboardPage } from "../pages/dashboard/DashboardPage";
import { GroupOverviewPage } from "../pages/group-overview/GroupOverviewPage";
import { LegacyCaseDetailsPage } from "../pages/legacy-case-details/LegacyCaseDetailsPage";
import { GroupsPage } from "../pages/groups/GroupsPage";
import { CreateGroupPage } from "../pages/groups/components/create/CreateGroupPage";
import { GroupDetailsPage } from "../pages/groups/components/details/GroupDetailsPage";
import { LandingPage } from "../pages/landing/LandingPage";
import { CaseOrderPage } from "../pages/order/CaseOrderPage";
import { PackageDetails } from "../pages/packages/PackageDetails";
import { PackageListView } from "../pages/packages/PackageListView";
import { PackageCreationPage } from "../pages/packages/package-creation/PackageCreationPage";
import { PeopleProfilePage } from "../pages/person-profile/PeopleProfilePage";
import { ProfilesListViewPage } from "../pages/profiles/ProfilesListViewPage";
import { Tasks } from "../pages/tasks/Tasks";
import { CreateAdjudicationPage } from "../pages/adjudication/CreateAdjudicationPage";
import { AdjudicationListViewPage } from "../pages/adjudication/adjudication-list-view/AdjudicationListViewPage";
import { AdjudicationPage } from "../pages/adjudication/adjudication-details/AdjudicationPage";
import { QuickscreenPage } from "../pages/quickscreen-case/QuickscreenPage";
import { MonitoringApplicantDetailPage } from "../pages/monitoring/MonitoringApplicantDetailPage";
import { MonitoringPage } from "../pages/monitoring/MonitoringPage";
import { EnrollApplicantsPage } from "../pages/monitoring/components/EnrollApplicantsPage";
import { useCanEnrollInMonitoring } from "../pages/monitoring/useCanEnrollInMonitoring";
import { useCanViewMonitoring } from "../pages/monitoring/useCanViewMonitoring";

function GatedCaseDetailsPage() {
  const canView = useCanViewCaseDetails();
  if (!canView) return <Navigate to="/" replace />;
  return <CaseDetailsPage />;
}

function GatedEnrollApplicantsPage() {
  const canEnroll = useCanEnrollInMonitoring();
  if (!canEnroll) return <Navigate to="/monitoring" replace />;
  return <EnrollApplicantsPage />;
}

function GatedMonitoringPage() {
  const { clientPortalEnableContinuousMonitoring } = useFlags();
  const canViewMonitoring = useCanViewMonitoring();
  if (!clientPortalEnableContinuousMonitoring || !canViewMonitoring)
    return <Navigate to="/" replace />;
  return (
    <Routes>
      <Route index element={<MonitoringPage />} />
      <Route path="enroll" element={<GatedEnrollApplicantsPage />} />
      <Route path=":subjectId" element={<MonitoringApplicantDetailPage />} />
      <Route path="*" element={<Navigate to="/monitoring" replace />} />
    </Routes>
  );
}

export function ProtectedRoutes() {
  const { t } = useTranslation();

  return (
    <AuthOutlet
      subtitle={t(
        "Authenticate your account by logging in with Certn's SSO provider."
      )}
    >
      <SessionTimeoutMonitor />
      <ClientPortalBootstrapProvider>
        <BootstrapAuthorizedInstrumentation />

        <Routes>
          <Route element={<BasePage />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/tasks/*" element={<Tasks />} />
            <Route path="/order/*" element={<CaseOrderPage />} />

            <Route path="/groups" element={<GroupsPage />} />
            <Route path="/groups/:groupId/*" element={<GroupDetailsPage />} />
            <Route path="/group-overview/*" element={<GroupOverviewPage />} />
            <Route path="/cases/*" element={<GroupOverviewPage />} />
            <Route path="/monitoring/*" element={<GatedMonitoringPage />} />

            <Route path="/groups/create" element={<CreateGroupPage />} />
            <Route
              path="/case-details/:caseId/*"
              element={<GatedCaseDetailsPage />}
            />
            <Route
              path="/quickscreen/:caseId/:sectionName"
              element={<QuickscreenPage />}
            />
            <Route
              path="/quickscreen/:caseId/*"
              element={<QuickscreenPage />}
            />
            <Route
              path="/legacy-case-details/:reportId/*"
              element={<LegacyCaseDetailsPage />}
            />
            <Route path="/profiles" element={<ProfilesListViewPage />} />
            <Route path="/packages" element={<PackageListView />} />
            <Route path="/packages/create" element={<PackageCreationPage />} />
            <Route path="/package/:packageId/" element={<PackageDetails />} />
            <Route path="/apply-links" element={<ApplyLinkListView />} />
            <Route path="/apply-link/create" element={<CreateApplyLink />} />
            <Route
              path="/apply-link/:applyLinkId/"
              element={<ApplyLinkDetails />}
            />
            <Route
              path="/adjudication/create"
              element={<CreateAdjudicationPage />}
            />
            <Route path="/billing/*" element={<BillingRoutes />} />
            <Route element={<RequireSettingsAccess />}>
              <Route path="/settings/*" element={<SettingsRoutes />} />
            </Route>
            <Route
              path="/adjudication"
              element={<AdjudicationListViewPage />}
            />
            <Route
              path="/adjudication/:adjudicationId/*"
              element={<AdjudicationPage />}
            />
            <Route
              path="/profiles/:subjectID"
              element={<PeopleProfilePage />}
            />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="*" element={<PageNotFoundError />} />
          </Route>
        </Routes>
      </ClientPortalBootstrapProvider>
    </AuthOutlet>
  );
}
